import { ref } from "vue";
import mitt from "mitt";

const emitter = mitt();

const LOCATION_CACHE_KEY = "previous_current_location";
const LOCATION_TTL = 30 * 1000; // 30 seconds

export const useLocationFinder = () => {
  const isLoading = ref(false);
  const isLoadingMessage = ref("");

  // -------------------- Cache Helpers --------------------

  const getCachedLocation = () => {
    try {
      const raw = sessionStorage.getItem(LOCATION_CACHE_KEY);
      if (!raw) return null;

      const cached = JSON.parse(raw);

      if (Date.now() - cached.timestamp > LOCATION_TTL) {
        sessionStorage.removeItem(LOCATION_CACHE_KEY);
        return null;
      }

      return cached;
    } catch {
      return null;
    }
  };

  const setCachedLocation = (latitude: number, longitude: number) => {
    try {
      sessionStorage.setItem(
        LOCATION_CACHE_KEY,
        JSON.stringify({
          latitude,
          longitude,
          timestamp: Date.now(),
        })
      );
    } catch { }
  };

  // -------------------- Geolocation Promise Wrapper --------------------

  const getPosition = (options: PositionOptions) => {
    return new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });
  };

  // -------------------- Main API --------------------

  const getCurrentLocation = async () => {
    isLoading.value = true;
    isLoadingMessage.value = "Fetching your current location. Please wait...";

    try {
      if (!navigator?.geolocation) {
        throw new Error("Location not supported");
      }

      // 🚀 1️⃣ Use cache if available
      const cached = getCachedLocation();
      if (cached) {
        emitter.emit("success", {
          latitude: cached.latitude,
          longitude: cached.longitude,
        });
        return;
      }

      // 🚀 2️⃣ Check permission (browser)
      if (navigator.permissions) {
        try {
          const permission = await navigator.permissions.query({
            name: "geolocation" as PermissionName,
          });

          if (permission.state === "denied") {
            throw new Error("PERMISSION_DENIED");
          }
        } catch { }
      }

      let position: GeolocationPosition;

      // 🚀 3️⃣ Fast location first
      try {
        position = await getPosition({
          enableHighAccuracy: false,
          timeout: 8000,
          maximumAge: 5 * 60 * 1000,
        });
      } catch {
        // 🚀 4️⃣ Fallback to accurate GPS
        position = await getPosition({
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        });
      }

      const { latitude, longitude } = position.coords;

      setCachedLocation(latitude, longitude);

      emitter.emit("success", {
        latitude,
        longitude,
      });
    } catch (err: any) {
      console.error("Geolocation error:", err);

      let message =
        "Unable to get your location. Please make sure Location/GPS is enabled and try again.";

      if (err?.code === 1 || err?.message === "PERMISSION_DENIED") {
        message =
          "Permission denied. Go to App Settings (or) Browser Settings > Permissions > Enable Location Access.";
      }

      if (err?.code === 3) {
        message =
          "Location request timed out. Please ensure GPS is enabled or select location manually.";
      }

      emitter.emit("error", message);
    } finally {
      isLoading.value = false;
      isLoadingMessage.value = "";
    }
  };

  return {
    getCurrentLocation,
    isLoading,
    isLoadingMessage,
    emitter,
  };
};